import { ChangeDetectionStrategy, Component, inject, input, signal } from '@angular/core';
import { ButtonComponent, DialogRef, IconComponent, InputComponent } from 'nebula-ui-kit';

export type VaultReasonConfirmDialogVariant = 'success' | 'error' | 'warning' | 'info';

@Component({
  selector: 'lib-vault-reason-confirm-dialog',
  standalone: true,
  imports: [ButtonComponent, IconComponent, InputComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="confirm-dialog"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="vault-reason-dialog-title"
      aria-describedby="vault-reason-dialog-message"
    >
      <div class="confirm-header">
        <div class="icon-container" [class]="typeClass()">
          <lib-icon name="Info" [size]="44" />
        </div>
        <div class="title-container">
          <h2 class="confirm-title" id="vault-reason-dialog-title">{{ title() }}</h2>
          <p class="confirm-message" id="vault-reason-dialog-message">{{ message() }}</p>
        </div>
      </div>

      <lib-input
        [label]="reasonLabel()"
        [placeholder]="reasonPlaceholder()"
        [value]="reason()"
        (valueChange)="reason.set($event)"
      />

      <div class="confirm-footer">
        <lib-button variant="secondary" (clicked)="onCancel()">{{ cancelText() }}</lib-button>
        <lib-button [variant]="confirmVariant()" (clicked)="onConfirm()">{{ confirmText() }}</lib-button>
      </div>
    </div>
  `,
  styles: [
    `
      .confirm-dialog {
        padding: var(--lib-spacing-lg, 24px);
        background-color: var(--md-sys-color-surface);
        border-radius: var(--lib-border-radius, 12px);
        font-family: var(--lib-font-family, Open Sans, sans-serif);
      }
      .confirm-header {
        display: flex;
        gap: var(--lib-spacing-md, 16px);
        margin-bottom: var(--lib-spacing-md, 16px);
      }
      .icon-container {
        flex-shrink: 0;
        width: 44px;
        height: 44px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .icon-container.type-info {
        color: var(--md-sys-color-info);
      }
      .icon-container.type-success {
        color: var(--md-sys-color-success);
      }
      .icon-container.type-warning {
        color: var(--md-sys-color-warning);
      }
      .icon-container.type-error {
        color: var(--md-sys-color-error);
      }
      .title-container {
        display: flex;
        flex-direction: column;
        gap: var(--lib-spacing-xs, 4px);
      }
      .confirm-title {
        margin: 0;
        font-size: 1.25rem;
        font-weight: 700;
      }
      .confirm-dialog:has(.type-info) .confirm-title {
        color: var(--md-sys-color-info);
      }
      .confirm-dialog:has(.type-success) .confirm-title {
        color: var(--md-sys-color-success);
      }
      .confirm-dialog:has(.type-warning) .confirm-title {
        color: var(--md-sys-color-warning);
      }
      .confirm-dialog:has(.type-error) .confirm-title {
        color: var(--md-sys-color-error);
      }
      .confirm-message {
        margin: 0;
        font-size: 0.9375rem;
        line-height: 1.5;
        color: var(--md-sys-color-on-surface-variant);
      }
      .confirm-footer {
        display: flex;
        justify-content: flex-end;
        gap: var(--lib-spacing-md, 16px);
        margin-top: var(--lib-spacing-lg, 24px);
      }
    `,
  ],
})
export class VaultReasonConfirmDialogComponent {
  readonly title = input.required<string>();
  readonly message = input.required<string>();
  readonly type = input<VaultReasonConfirmDialogVariant>('warning');
  readonly confirmText = input('Confirmar');
  readonly cancelText = input('Cancelar');
  readonly reasonLabel = input('Motivo');
  readonly reasonPlaceholder = input('');
  readonly requireReason = input(false);

  readonly reason = signal('');

  private readonly dialogRef = inject(DialogRef<string | null>);

  typeClass(): string {
    return `type-${this.type()}`;
  }

  confirmVariant(): 'primary' | 'danger' {
    return this.type() === 'error' ? 'danger' : 'primary';
  }

  onConfirm(): void {
    const value = this.reason().trim();
    if (this.requireReason() && !value) {
      return;
    }
    this.dialogRef.close(value);
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }
}
